<template>
  <div class="documents-content">
    <div
      v-for="document in documentsArray"
      v-if="document.show"
      :key="document.fileUrl"
      class="document-item"
    >
      <div class="document-title-wrap">
        <span
          :class="['document-title', getExpirationStatusClass(document.expirationMetaKey)]"
        >
          {{ document.title }}
        </span>
      </div>
      <div class="document-expiration-date-wrap">
        <span
          v-if="document.expirationMetaKey && getExpirationDate(document.expirationMetaKey)"
          :class="['document-expiration-date', getExpirationStatusClass(document.expirationMetaKey)]"
        >
          <p class="ma-0 text-right">{{ $t('components.documents_content.expiration_date') }} </p>
          <p class="ma-0 text-right">{{ getExpirationDate(document.expirationMetaKey) }}</p>
        </span>
        <a
          target="_blank"
          :class="[
            'document-button',
            getExpirationStatusClass(document.expirationMetaKey),
            { 'disabled': !documentIsPresent(businessDocuments[document.fileUrl]) },
          ]"
          @click="goToDocumentsPath(businessDocuments[document.fileUrl].url)"
        >
          {{ $t('components.documents_content.show') }}
        </a>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex';
import CustomTooltip from '@/components/tooltip/CustomTooltip.vue';

export default {
  name: 'DocumentsContent',
  components: {
    CustomTooltip,
  },
  props: {
    businessDocuments: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    documentsArray() {
      return [
        {
          title: this.$t('components.documents_content.social_contract'),
          fileUrl: 'social_contract',
          show: true,
        },
        {
          title: this.$t('components.documents_content.business_license'),
          fileUrl: 'business_license',
          expirationMetaKey: 'business_license_expiration',
          show: true,
        },
        {
          title: this.$t('components.documents_content.balance_calibration_certificate'),
          fileUrl: 'balance_calibration_certificate',
          show: true,
        },
        {
          title: this.$t('components.documents_content.photographic_register'),
          fileUrl: 'photograph_register',
          show: true,
        },
        {
          title: this.$t('components.documents_content.operation_photograph_register'),
          fileUrl: 'operation_photograph_register',
          show: true,
        },
        {
          title: this.$t('components.documents_content.environment_license'),
          fileUrl: 'environment_license',
          expirationMetaKey: 'environment_license_expiration',
          show: !this.businessDocuments.has_environment_exemption,
        },
        {
          title: this.$t('components.documents_content.exemption_environmental_license'),
          fileUrl: 'exemption_environmental_license',
          show: this.businessDocuments.has_environment_exemption,
        },
        {
          title: this.$t('components.documents_content.fire_department_license'),
          fileUrl: 'fire_department_license',
          show: true,
        },
        {
          title: this.$t('components.documents_content.sorting_area_photo'),
          fileUrl: 'sorting_area_photo',
          show: true,
        },
        {
          title: this.$t('components.documents_content.storage_disposal_photo'),
          fileUrl: 'storage_disposal_photo',
          show: true,
        },
        {
          title: this.$t('components.documents_content.materials_exit_photo'),
          fileUrl: 'materials_exit_photo',
          show: true,
        },
        {
          title: this.$t('components.documents_content.equipment_photo'),
          fileUrl: 'equipment_photo',
          show: true,
        },
      ];
    },
  },

  methods: {
    ...mapActions(['addErrorMessage']),

    getExpiration(expirationMetaKey) {
      return this.businessDocuments[expirationMetaKey];
    },

    getExpirationStatusClass(expirationMetaKey) {
      const expiration = this.getExpiration(expirationMetaKey);

      if (!expiration) return null;

      const status = String(expiration.status || '').toLowerCase();

      if (status === 'expired') return 'expiration-expired';
      if (status === 'warning') return 'expiration-warning';

      return null;
    },

    getExpirationDate(expirationMetaKey) {
      if (!expirationMetaKey) return '';

      const expiration = this.getExpiration(expirationMetaKey);

      return expiration && expiration.date ? String(expiration.date).trim() : '';
    },

    goToDocumentsPath(file) {
      if (file) {
        window.open(file);
      } else {
        this.addErrorMessage(this.$t('errors.document_not_send'));
      }
    },

    documentIsPresent(file) {
      return file && file?.url;
    },
  },
};
</script>
<style lang="scss" scoped>
  .documents-content {

    .document-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      margin: 0 0 20px 0;

      .document-button {
        width: 150px;
        height: 50px;
        background-color: transparent;
        border: 2px $color_primary solid;
        color: $color_primary;
        font-size: 1em;
        font-weight: bold;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-decoration: none;

        &.expiration-expired {
          color: $color_disapproved;
          border-color: $color_disapproved;
          background-color: transparent;
        }

        &.expiration-warning {
          color: $color_pending;
          border-color: $color_pending;
          background-color: transparent;
        }

        &.disabled {
          color: rgba($color_gray, 0.6);
          border: 2px rgba($color_gray, 0.6) solid;

        }
      }

      .document-expiration-date-wrap {
        display: flex;
        align-items: center;
        gap: 10px;

        .document-expiration-date {
          color: $color_primary;

          &.expiration-expired {
            color: $color_disapproved;
          }

          &.expiration-warning {
            color: $color_pending;
          }
        }
      }

    }

    .document-title-wrap {
      display: flex;
      align-items: center;
      gap: 10px;
      flex: 1;
      min-width: 0;
    }

    .document-title {
      font-weight: bold;
      font-size: 1em;

      &.expiration-expired {
        color: $color_disapproved;
      }

      &.expiration-warning {
        color: $color_pending;
      }
    }

    .document-expiration-help {
      display: inline-flex;
      flex-shrink: 0;
      cursor: help;
      outline: none;
      color: $color_secondary;

      &:focus-visible .document-expiration-help__mark {
        box-shadow: 0 0 0 2px rgba($color_primary, 0.45);
      }

      &__mark {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.35rem;
        height: 1.35rem;
        border-radius: 50%;
        border: 2px solid currentColor;
        font-size: 0.8rem;
        font-weight: bold;
        line-height: 1;
        user-select: none;
      }
    }
  }
</style>
